import { projectTimelineEventTypes, refParamTypes } from '@/Models';
import Resources from '@/Resources';
import { displayNameFromResources } from '@/Utils/EnumUtility';
import { addRefWithOptions } from '@/Utils/UrlUtility';
import { camelCaseToKebabCase } from '@/Utils/StringUtility';
import Config from '@/Config';

const helpUrls = Config.helpUrls;

const {
    upcoming,
    crowdfunding,
    funded,
    latePledge,
    pledgeManagerOpen,
    pledgeManagerLimited,
    fulfillment,
    canceled,
    failed,
    closed,
    latePledgeEnded,
} = projectTimelineEventTypes;

export const actionTypes = {
    follow: 1,
    backNow: 2,
    goToYourPledge: 3,
};

export const userEngagmentLevelTypes = {
    unlogged: 1,
    uninvolved: 2,
    follower: 3,
    backer: 4,
};

export const badgeModelTypes = {
    now: 1,
    next: 2,
    none: 3,
};

export function addProjectTimelineRef(url) {
    return addRefWithOptions(url, { page: refParamTypes.projectRef, section: 'home', suffix: 'project-timeline' });
}

function buildAnalyticsSectionLabels() {
    return Object.keys(projectTimelineEventTypes).reduce((acc, key) => {
        if (typeof projectTimelineEventTypes[key] === 'number') {
            acc[key] = `project-timeline-${camelCaseToKebabCase(key)}`;
        }
        return acc;
    }, {});
}

function buildAnalyticEventPayload(projectType, actionType) {
    const action = {
        [actionTypes.backNow]: 'back-now',
        [actionTypes.goToYourPledge]: 'go-to-your-pledge',
    }[actionType];

    const section = buildAnalyticsSectionLabels()[projectType];

    if (!action) return null;

    return {
        event: 'project-navigate',
        properties: {
            section,
            action,
        },
    };
}

function buildCta(type, projectType) {
    return {
        type,
        analyticEventPayload: buildAnalyticEventPayload(projectType, type),
    };
}

function buildCopyObject({ defaultCopy, backerCopy, backerWithoutBackerNumberCopy }) {
    return {
        [userEngagmentLevelTypes.unlogged]: defaultCopy,
        [userEngagmentLevelTypes.uninvolved]: defaultCopy,
        [userEngagmentLevelTypes.follower]: defaultCopy,
        [userEngagmentLevelTypes.backer]: {
            backerWithoutBackerNumberCopy,
            default: backerCopy || defaultCopy,
        },
    };
}

function buildCtaObject(ctaType, projectType) {
    const actions = {
        [actionTypes.follow]: buildCta(actionTypes.follow, projectType),
        [actionTypes.backNow]: buildCta(actionTypes.backNow, projectType),
        [actionTypes.goToYourPledge]: buildCta(actionTypes.goToYourPledge, projectType),
    };

    return {
        [actionTypes.follow]: {
            [userEngagmentLevelTypes.unlogged]: actions[actionTypes.follow],
            [userEngagmentLevelTypes.uninvolved]: actions[actionTypes.follow],
            [userEngagmentLevelTypes.follower]: null,
            [userEngagmentLevelTypes.backer]: null,
        },
        [actionTypes.backNow]: {
            [userEngagmentLevelTypes.unlogged]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.uninvolved]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.follower]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.backer]: null,
        },
        [actionTypes.goToYourPledge]: {
            [userEngagmentLevelTypes.unlogged]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.uninvolved]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.follower]: actions[actionTypes.backNow],
            [userEngagmentLevelTypes.backer]: actions[actionTypes.goToYourPledge],
        },
    }[ctaType];
}

function createCampaignStatus(
    projectTypeKey, {
        defaultCopyKey,
        backerCopyKey,
        backerWithoutBackerNumberCopyKey,
        ctaType,
        helpUrl,
        showAdditionalInfo = false,
    },
) {
    const defaultCopy = Resources.ProjectHome[defaultCopyKey];
    const backerCopy = backerCopyKey ? Resources.ProjectHome[backerCopyKey] : null;
    const backerWithoutBackerNumberCopy = backerWithoutBackerNumberCopyKey ? Resources.ProjectHome[backerWithoutBackerNumberCopyKey] : null;
    const learnMoreUrl = helpUrl ? addProjectTimelineRef(helpUrl) : null;

    return {
        status: displayNameFromResources(Resources.ProjectHome, projectTimelineEventTypes[projectTimelineEventTypes[projectTypeKey]]),
        copy: buildCopyObject({
            defaultCopy,
            backerCopy,
            backerWithoutBackerNumberCopy,
        }),
        cta: buildCtaObject(ctaType, projectTypeKey),
        learnMoreUrl,
        showAdditionalInfo,
    };
}

export const campaignStatuses = {
    [upcoming]: createCampaignStatus('upcoming', {
        defaultCopyKey: 'ProjectTimelineUpcomingMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.notifyOnLaunch,
    }),
    [crowdfunding]: createCampaignStatus('crowdfunding', {
        defaultCopyKey: 'ProjectTimelineCrowdfundingNotBackerMessage',
        backerCopyKey: 'ProjectTimelineCrowdfundingBackerMessage',
        backerWithoutBackerNumberCopyKey: 'ProjectTimelineCrowdfundingBackerWithoutBackerNumberMessage',
        ctaType: actionTypes.backNow,
        helpUrl: helpUrls.help.howCanIPledgeCrowdfunding,
        showAdditionalInfo: true,
    }),
    [funded]: createCampaignStatus('funded', {
        defaultCopyKey: 'ProjectTimelineFundingMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.charging,
    }),
    [latePledge]: createCampaignStatus('latePledge', {
        defaultCopyKey: 'ProjectTimelineLatePledgeNotBackerMessage',
        backerCopyKey: 'ProjectTimelineLatePledgeBackerMessage',
        ctaType: actionTypes.goToYourPledge,
        helpUrl: helpUrls.help.latePledges,
        showAdditionalInfo: true,
    }),
    [pledgeManagerOpen]: createCampaignStatus('pledgeManagerOpen', {
        defaultCopyKey: 'ProjectTimelinePledgeManagerOpenNotBackerMessage',
        backerCopyKey: 'ProjectTimelinePledgeManagerOpenBackerMessage',
        ctaType: actionTypes.goToYourPledge,
        helpUrl: helpUrls.help.howCanIPledgePledgeManager,
        showAdditionalInfo: true,
    }),
    [pledgeManagerLimited]: createCampaignStatus('pledgeManagerLimited', {
        defaultCopyKey: 'ProjectTimelinePledgeManagerLimitedMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.howCanIPledgePledgeManager,
    }),
    [fulfillment]: createCampaignStatus('fulfillment', {
        defaultCopyKey: 'ProjectTimelineFulfillmentNotBackerMessage',
        backerCopyKey: 'ProjectTimelineFulfillmentBackerMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.shippingCost,
        showAdditionalInfo: true,
    }),
    [canceled]: createCampaignStatus('canceled', {
        defaultCopyKey: 'ProjectTimelineCanceledNotBackerMessage',
        backerCopyKey: 'ProjectTimelineCanceledBackerMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.projectFail,
    }),
    [failed]: createCampaignStatus('failed', {
        defaultCopyKey: 'ProjectTimelineFailedNotBackerMessage',
        backerCopyKey: 'ProjectTimelineFailedBackerMessage',
        ctaType: actionTypes.follow,
        helpUrl: helpUrls.help.projectFail,
    }),
    [closed]: createCampaignStatus('closed', {
        defaultCopyKey: 'ProjectTimelineClosedMessage',
        ctaType: actionTypes.follow,
    }),
    [latePledgeEnded]: createCampaignStatus('latePledgeEnded', {
        defaultCopyKey: 'ProjectTimelineLatePledgeEndedMessage',
        ctaType: actionTypes.follow,
    }),
};

export function buildTimelineCardBadgeModel(type) {
    const baseClasses = 'gfu-project-timeline-card__badge _px-2 _tc--white _fw-b _ttu';

    const badges = {
        [badgeModelTypes.now]: {
            text: Resources.ProjectHome.ProjectTimelineNowLabel,
            icon: 'spinner',
            classes: `${baseClasses} _bgc--accent`,
        },
        [badgeModelTypes.next]: {
            text: Resources.ProjectHome.ProjectTimelineNextStepLabel,
            icon: 'arrow-right-long-to-line',
            classes: `${baseClasses} gfu-project-timeline-card__badge--muted`,
        },
        [badgeModelTypes.none]: {
            text: '',
            icon: '',
            classes: `${baseClasses} _bgc--default`,
        },
    };

    return badges[type];
}

function buildTimelineCardModel(timelineEvent, userEngagement = userEngagmentLevelTypes.unlogged, backerNumber) {
    let copy = null;

    if (typeof timelineEvent.copy[userEngagement] === 'object') {
        if (!backerNumber  && timelineEvent.type === crowdfunding) {
            copy = timelineEvent.copy[userEngagement].backerWithoutBackerNumberCopy;
        }
        else if (backerNumber  && timelineEvent.type === crowdfunding) {
            copy = timelineEvent.copy[userEngagement].backerCopyKey;
        }
        else {
            copy = timelineEvent.copy[userEngagement].default;
        }
    }
    else {
        copy = timelineEvent.copy[userEngagement];
    }

    return {
        ...timelineEvent,
        copy,
        cta: timelineEvent.cta[userEngagement],
    };
}

export function buildSectionTimelineModel(timelineEvents, userEngagement, backerNumber) {
    return timelineEvents.map(({ endAt, isActive, startAt, tooltipText, type, isNext }) => {
        const eventAdditionalData = campaignStatuses[type];

        return buildTimelineCardModel(
            {
                endAt,
                isActive,
                startAt,
                tooltipText,
                type,
                isNext,
                ...eventAdditionalData,
            },
            userEngagement,
            backerNumber,
        );
    });
}